/**
 * NORTHFORM — Premium Shopify 2.0 Theme
 * Preserves exact 3D hero, 3D showcase, animations from original HTML
 * + Production Shopify AJAX cart, product variants, 3D media, US localization
 */

class NorthFormTheme {
  constructor() {
    this.cartDrawer = document.querySelector('[data-cart-drawer]');
    this.cartOverlay = document.querySelector('[data-cart-overlay]');
    this.searchOverlay = document.querySelector('[data-search-overlay]');
    this.newsletterPopup = document.querySelector('[data-newsletter-popup]');
    this.init();
  }

  init() {
    this.initReveal();
    this.initHero3D();
    this.initShowcase3D();
    this.initCart();
    this.initProductForm();
    this.initSearch();
    this.initNewsletterPopup();
    this.initAccordions();
    this.initStickyATC();
    this.initMediaGallery();
    this.initWishlist();
    this.initQuickAdd();
    this.initFilters();
    console.log('%cNORTHFORM Shopify 2.0 — USD • Made in CA', 'background:#0F0F0F;color:#fff;padding:6px 10px;border-radius:6px;font-weight:700');
  }

  // Reveal on scroll
  initReveal() {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('in'); });
    }, { threshold: 0.12 });
    document.querySelectorAll('.reveal, .product-card, .why-card, .review-card').forEach(el => {
      el.classList.add('reveal');
      io.observe(el);
    });
  }

  // Hero 3D parallax — preserved exactly
  initHero3D() {
    document.querySelectorAll('[id^="heroVisual-"]').forEach(visual => {
      const stage = visual.querySelector('[id^="heroStage-"]') || visual.querySelector('.hero-stage');
      if (!stage) return;
      visual.addEventListener('mousemove', (e) => {
        const r = visual.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        stage.style.transform = `rotateY(${x * 18}deg) rotateX(${-y * 12}deg) translateZ(0)`;
      });
      visual.addEventListener('mouseleave', () => stage.style.transform = '');
    });
  }

  // Interactive 3D showcase drag — preserved
  initShowcase3D() {
    document.querySelectorAll('[id^="dragArea-"]').forEach(dragArea => {
      const interactive = dragArea.querySelector('[id^="interactiveProduct-"]') || dragArea.querySelector('[data-showcase-image]') || dragArea.querySelector('.showcase-stage');
      if (!interactive) return;
      let isDown = false, startX = 0, rotY = -12, rotX = 6, curRotY = -12, scale = 1;
      const isImage = interactive.tagName === 'IMG';
      
      dragArea.addEventListener('pointerdown', e => {
        isDown = true;
        startX = e.clientX;
        dragArea.setPointerCapture(e.pointerId);
        dragArea.style.cursor = 'grabbing';
      });
      dragArea.addEventListener('pointermove', e => {
        if (!isDown) return;
        const dx = e.clientX - startX;
        curRotY = rotY + dx * 0.35;
        if (isImage) {
          interactive.style.transform = `perspective(1000px) rotateY(${curRotY}deg) scale(${scale})`;
        } else {
          interactive.style.transform = `rotateY(${curRotY}deg) rotateX(${rotX}deg) scale(${scale})`;
        }
      });
      const end = () => { if (!isDown) return; isDown = false; rotY = curRotY; dragArea.style.cursor = 'grab'; };
      dragArea.addEventListener('pointerup', end);
      dragArea.addEventListener('pointercancel', end);
      dragArea.addEventListener('wheel', e => {
        e.preventDefault();
        scale = Math.min(1.9, Math.max(0.9, scale + e.deltaY * -0.001));
        if (isImage) {
          interactive.style.transform = `perspective(1000px) rotateY(${curRotY}deg) scale(${scale})`;
        } else {
          interactive.style.transform = `rotateY(${curRotY}deg) rotateX(${rotX}deg) scale(${scale})`;
        }
      }, { passive: false });

      // Gentle auto rotate when idle (preserves original premium feel)
      setInterval(() => {
        if (!isDown) {
          curRotY += 0.15;
          rotY = curRotY;
          if (isImage) {
            interactive.style.transform = `perspective(1000px) rotateY(${curRotY}deg) scale(${scale})`;
          } else {
            interactive.style.transform = `rotateY(${curRotY}deg) rotateX(${rotX}deg) scale(${scale})`;
          }
        }
      }, 30);
    });
  }

  // Cart - Shopify AJAX
  initCart() {
    // Open/Close
    document.querySelectorAll('[data-cart-open]').forEach(btn => {
      btn.addEventListener('click', (e) => { e.preventDefault(); this.openCart(); });
    });
    document.querySelectorAll('[data-cart-close], [data-cart-overlay]').forEach(el => {
      el.addEventListener('click', () => this.closeCart());
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') { this.closeCart(); this.closeSearch(); }
    });

    // Delegate quantity changes
    document.addEventListener('click', e => {
      const minus = e.target.closest('[data-cart-qty-minus]');
      const plus = e.target.closest('[data-cart-qty-plus]');
      const remove = e.target.closest('[data-cart-remove]');
      if (minus) { e.preventDefault(); this.changeCartQty(minus.dataset.key, -1); }
      if (plus) { e.preventDefault(); this.changeCartQty(plus.dataset.key, 1); }
      if (remove) { e.preventDefault(); this.removeCartItem(remove.dataset.key); }
    });

    // Discount code
    const discountBtn = document.querySelector('[data-discount-apply]');
    if (discountBtn) {
      discountBtn.addEventListener('click', () => {
        const input = document.querySelector('[data-discount-input]');
        if (!input || !input.value) return;
        window.location.href = `/discount/${encodeURIComponent(input.value.trim().toUpperCase())}`;
      });
    }
  }

  async fetchCart() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    return await res.json();
  }

  async openCart() {
    if (!this.cartDrawer) return;
    this.cartDrawer.classList.add('open');
    if (this.cartOverlay) this.cartOverlay.classList.add('open');
    document.body.style.overflow = 'hidden';
    // Refresh cart
    const cart = await this.fetchCart();
    this.renderCart(cart);
  }

  closeCart() {
    if (this.cartDrawer) this.cartDrawer.classList.remove('open');
    if (this.cartOverlay) this.cartOverlay.classList.remove('open');
    document.body.style.overflow = '';
  }

  async addToCart(variantId, quantity = 1, properties = {}) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);
    for (const [k, v] of Object.entries(properties)) formData.append(`properties[${k}]`, v);

    try {
      const res = await fetch('/cart/add.js', { method: 'POST', body: formData, headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || 'Add to cart failed');
      
      // Haptic micro-animation
      const badge = document.querySelector('[data-cart-count-bubble]');
      if (badge) { badge.style.transform = 'scale(1.3)'; setTimeout(() => badge.style.transform = '', 200); }

      const cart = await this.fetchCart();
      this.renderCart(cart);
      this.openCart();
      return data;
    } catch (err) {
      console.error('[NORTHFORM] Add to cart error', err);
      alert(err.message || 'Could not add to cart. Please try again.');
    }
  }

  async changeCartQty(key, delta) {
    const cart = await this.fetchCart();
    const item = cart.items.find(i => i.key === key);
    if (!item) return;
    const newQty = item.quantity + delta;
    if (newQty <= 0) { this.removeCartItem(key); return; }

    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: newQty })
    });
    const updated = await this.fetchCart();
    this.renderCart(updated);
  }

  async removeCartItem(key) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
    const updated = await this.fetchCart();
    this.renderCart(updated);
  }

  renderCart(cart) {
    // Update counts
    document.querySelectorAll('[data-cart-count], [data-cart-count-bubble]').forEach(el => {
      if (el.hasAttribute('data-cart-count-bubble')) {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count === 0 ? 'none' : 'grid';
      } else {
        el.textContent = `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`;
      }
    });
    // Update subtotal
    document.querySelectorAll('[data-cart-subtotal]').forEach(el => {
      el.textContent = this.formatMoney(cart.total_price);
    });
    // Free shipping progress
    const freeThreshold = (window.NorthformSettings && window.NorthformSettings.freeShippingThreshold) || 7500;
    const remaining = freeThreshold - cart.total_price;
    const progress = Math.min(100, (cart.total_price / freeThreshold) * 100);
    document.querySelectorAll('[data-ship-progress]').forEach(el => el.style.width = progress + '%');
    document.querySelectorAll('[data-free-ship-msg]').forEach(el => {
      el.innerHTML = remaining > 0 ? `Add <b>${this.formatMoney(remaining)}</b> more for free shipping.` : `🎉 You got free shipping!`;
    });
    // If on cart page, reload or update DOM - for simplicity, if cart items container exists and we are on cart page, we could reload
    // For drawer, we need to re-render items if we have a container
    const itemsContainer = document.querySelector('[data-cart-items]');
    if (itemsContainer && cart.items) {
      if (cart.item_count === 0) {
        itemsContainer.innerHTML = `
          <div style="text-align:center;padding:40px 20px">
            <div style="font-size:40px;margin-bottom:12px">◐</div>
            <h4 style="font-size:16px">Your cart is empty</h4>
            <p style="font-size:13px;color:var(--muted);margin:8px 0 16px">Add a lamp and see how your evenings change.</p>
            <a href="/collections/all" class="btn btn-secondary">SHOP BEST SELLERS</a>
          </div>`;
        const foot = document.querySelector('[data-cart-foot]');
        if (foot) foot.style.display = 'none';
      } else {
        itemsContainer.innerHTML = cart.items.map(item => `
          <div class="cart-item" data-key="${item.key}">
            <div class="cart-item-img">
              ${item.featured_image ? `<img src="${item.featured_image.url}?width=200" alt="${item.featured_image.alt}" loading="lazy" style="width:100%;height:100%;object-fit:contain">` : '◐'}
            </div>
            <div class="cart-item-info">
              <h4><a href="${item.url}">${this.escapeHtml(item.product_title)}</a></h4>
              <span style="font-size:11px;color:var(--muted)">${this.escapeHtml(item.variant_title || 'Default')} • ${item.product_type || ''}</span>
              <div class="qty-controls">
                <button class="qty-btn" data-cart-qty-minus data-key="${item.key}">−</button>
                <span style="font-size:13px;font-weight:600">${item.quantity}</span>
                <button class="qty-btn" data-cart-qty-plus data-key="${item.key}">+</button>
                <button style="margin-left:auto;font-size:11px;text-decoration:underline;color:var(--muted)" data-cart-remove data-key="${item.key}">Remove</button>
              </div>
            </div>
            <div style="font-weight:700;font-size:13px">${this.formatMoney(item.final_line_price)}</div>
          </div>
        `).join('');
      }
    }
  }

  // Product Form - Variant handling with Shopify
  initProductForm() {
    const form = document.querySelector('[data-product-form]');
    if (!form) return;

    const productJsonEl = document.querySelector('[data-product-json]');
    const variantsJsonEl = document.querySelector('[data-product-variants]');
    let productData = null;
    try {
      if (productJsonEl) productData = JSON.parse(productJsonEl.textContent);
    } catch(e) { console.warn('Product JSON parse failed', e); }

    // Variant radio handling
    document.querySelectorAll('[data-option-name]').forEach(input => {
      input.addEventListener('change', () => this.onVariantChange(productData));
    });

    // Quantity
    const qtyDisplay = form.querySelector('[data-qty-display]') || form.querySelector('[data-qty-value]');
    const qtyInput = form.querySelector('[data-quantity-input]') || form.querySelector('[data-qty-input]');
    const minusBtn = form.querySelector('[data-qty-minus]');
    const plusBtn = form.querySelector('[data-qty-plus]');

    let qty = 1;
    const setQty = (n) => {
      qty = Math.max(1, n);
      if (qtyDisplay) qtyDisplay.textContent = qty;
      if (qtyInput) qtyInput.value = qty;
    };

    if (minusBtn) minusBtn.addEventListener('click', () => setQty(qty - 1));
    if (plusBtn) plusBtn.addEventListener('click', () => setQty(qty + 1));

    // Add to cart submit
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const variantIdInput = form.querySelector('[data-variant-id]') || form.querySelector('input[name="id"]');
      const variantId = variantIdInput ? variantIdInput.value : null;
      const quantityInput = form.querySelector('[data-quantity-input]') || form.querySelector('input[name="quantity"]');
      const quantity = quantityInput ? parseInt(quantityInput.value) : 1;
      
      if (!variantId) { alert('Please select a variant'); return; }

      const btn = form.querySelector('[data-add-to-cart]');
      const originalText = btn ? btn.innerHTML : '';
      if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }

      await this.addToCart(variantId, quantity);

      if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); btn.innerHTML = originalText; }
    });

    // Sticky ATC
    const stickyBtn = document.querySelector('[data-sticky-add-to-cart]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        const variantIdInput = form.querySelector('[data-variant-id]') || form.querySelector('input[name="id"]');
        const qtyInput = form.querySelector('[data-quantity-input]') || form.querySelector('input[name="quantity"]');
        const variantId = variantIdInput ? variantIdInput.value : null;
        const quantity = qtyInput ? parseInt(qtyInput.value) : 1;
        if (variantId) this.addToCart(variantId, quantity);
      });
    }

    // Initial variant sync
    if (productData) this.onVariantChange(productData);
  }

  onVariantChange(product) {
    if (!product) return;
    const form = document.querySelector('[data-product-form]');
    if (!form) return;

    // Collect selected options
    const selectedOptions = [];
    form.querySelectorAll('[data-option-name]').forEach(input => {
      if (input.checked) selectedOptions.push(input.value);
    });
    // Legacy support for select dropdowns
    form.querySelectorAll('select[name^="options"]').forEach(sel => selectedOptions.push(sel.value));

    // Find matching variant
    const matched = product.variants.find(v => {
      return selectedOptions.every((opt, idx) => v.options[idx] === opt);
    }) || product.variants[0];

    if (!matched) return;

    // Update variant ID
    const variantIdInput = form.querySelector('[data-variant-id]') || form.querySelector('input[name="id"]');
    if (variantIdInput) variantIdInput.value = matched.id;

    // Update price
    const priceEl = document.querySelector('[data-product-price]') || document.querySelector('[data-sticky-price]');
    const priceEls = document.querySelectorAll('[data-product-price]');
    priceEls.forEach(el => el.textContent = this.formatMoney(matched.price));

    const compareEl = document.querySelector('[data-compare-price]');
    if (compareEl) {
      if (matched.compare_at_price > matched.price) {
        compareEl.textContent = this.formatMoney(matched.compare_at_price);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    // Update Add to Cart text
    const addText = document.querySelector('[data-add-to-cart-text]');
    if (addText) {
      addText.textContent = matched.available ? `ADD TO CART • ${this.formatMoney(matched.price)}` : 'SOLD OUT';
    }
    const btn = form.querySelector('[data-add-to-cart]');
    if (btn) btn.disabled = !matched.available;

    // Update option display
    selectedOptions.forEach((opt, i) => {
      const disp = form.querySelectorAll('[data-option-display]')[i];
      if (disp) disp.textContent = opt;
    });

    // Update active class
    form.querySelectorAll('.variant').forEach(v => v.classList.remove('active'));
    selectedOptions.forEach(opt => {
      form.querySelectorAll(`[data-option-value="${CSS.escape(opt)}"]`).forEach(label => {
        const lab = label.closest('.variant');
        if (lab) lab.classList.add('active');
      });
    });

    // Update URL
    if (history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', matched.id);
      history.replaceState({}, '', url.toString());
    }

    // Update featured media if variant has featured_media
    if (matched.featured_media && matched.featured_media.id) {
      this.updateMedia(matched.featured_media.id);
    }
  }

  updateMedia(mediaId) {
    const containers = document.querySelectorAll('[data-media-container]');
    const thumbs = document.querySelectorAll('[data-thumb]');
    containers.forEach(c => {
      c.style.display = c.dataset.mediaId == mediaId ? 'grid' : 'none';
      c.classList.toggle('active', c.dataset.mediaId == mediaId);
    });
    thumbs.forEach(t => {
      t.classList.toggle('active', t.dataset.mediaId == mediaId);
    });
  }

  initMediaGallery() {
    document.querySelectorAll('[data-thumb]').forEach(thumb => {
      thumb.addEventListener('click', () => {
        this.updateMedia(thumb.dataset.mediaId);
        // If 3D model, initialize
        if (thumb.dataset.mediaType === 'model') {
          const container = document.querySelector(`[data-media-container][data-media-id="${thumb.dataset.mediaId}"]`);
          if (container) {
            const modelViewer = container.querySelector('model-viewer');
            if (modelViewer) modelViewer.focus();
          }
        }
      });
    });

    // Keyboard for Shopify XR
    window.addEventListener('load', () => {
      if (window.ShopifyXR) {
        document.addEventListener('shopify_xr_initialized', () => {
          // XR ready
        });
      }
    });
  }

  initSearch() {
    const openBtns = document.querySelectorAll('[data-search-open]');
    const closeBtns = document.querySelectorAll('[data-search-close]');
    openBtns.forEach(btn => btn.addEventListener('click', (e) => { e.preventDefault(); this.openSearch(); }));
    closeBtns.forEach(btn => btn.addEventListener('click', () => this.closeSearch()));
    
    const input = document.querySelector('[data-search-input]');
    if (input) {
      let debounce;
      input.addEventListener('input', (e) => {
        clearTimeout(debounce);
        debounce = setTimeout(() => this.predictiveSearch(e.target.value), 300);
      });
    }
  }

  openSearch() {
    if (!this.searchOverlay) return;
    this.searchOverlay.style.display = 'block';
    document.body.style.overflow = 'hidden';
    const input = this.searchOverlay.querySelector('[data-search-input]');
    if (input) setTimeout(() => input.focus(), 100);
  }

  closeSearch() {
    if (!this.searchOverlay) return;
    this.searchOverlay.style.display = 'none';
    document.body.style.overflow = '';
  }

  async predictiveSearch(query) {
    if (!query || query.length < 2) return;
    // Use Search & Discovery API if available, else just let form submit handle
    // For now, simple client-side filter of collection if Shopify predictive not enabled
    // In production, would fetch `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`
    try {
      const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,variants.title`);
      if (!res.ok) return;
      const data = await res.json();
      const resultsContainer = document.querySelector('[data-search-results]');
      if (!resultsContainer || !data.resources?.results?.products) return;
      resultsContainer.innerHTML = data.resources.results.products.map(p => `
        <a href="${p.url}" style="background:#fff;border:1px solid var(--line);border-radius:16px;padding:10px;display:flex;gap:10px;align-items:center;text-decoration:none;color:inherit">
          <img src="${p.featured_image?.url || ''}?width=100" alt="${p.title}" loading="lazy" style="width:48px;height:58px;object-fit:contain;background:var(--bg2);border-radius:10px">
          <div style="font-size:12px"><b style="display:block">${p.title}</b>${p.price ? this.formatMoney(p.price) : ''}</div>
        </a>
      `).join('');
    } catch(e) { /* ignore */ }
  }

  initNewsletterPopup() {
    if (!this.newsletterPopup) return;
    if (localStorage.getItem('nf_popup_dismissed')) return;

    setTimeout(() => {
      this.newsletterPopup.style.opacity = '1';
      this.newsletterPopup.style.transform = 'translateY(0)';
      this.newsletterPopup.style.pointerEvents = 'auto';
    }, 3400);

    const closeBtn = this.newsletterPopup.querySelector('[data-newsletter-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.newsletterPopup.style.transform = 'translateY(16px)';
        this.newsletterPopup.style.opacity = '0';
        this.newsletterPopup.style.pointerEvents = 'none';
        localStorage.setItem('nf_popup_dismissed', '1');
      });
    }
  }

  initAccordions() {
    document.querySelectorAll('[data-accordion] .acc-head').forEach(head => {
      head.addEventListener('click', () => {
        const item = head.closest('[data-accordion]');
        const body = item.querySelector('.acc-body');
        const icon = item.querySelector('[data-accordion-icon]');
        const isOpen = body.style.display !== 'none';
        // Close others if needed? Keep multiple open per original
        if (isOpen) {
          body.style.display = 'none';
          if (icon) icon.textContent = '+';
          item.classList.remove('open');
        } else {
          body.style.display = 'block';
          if (icon) icon.textContent = '−';
          item.classList.add('open');
        }
      });
    });
  }

  initStickyATC() {
    const sticky = document.getElementById('sticky-atc');
    if (!sticky) return;
    let lastY = 0;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      if (y > 900) {
        sticky.style.transform = 'translateY(0)';
        sticky.style.display = 'flex';
      } else {
        sticky.style.transform = 'translateY(110%)';
      }
      lastY = y;
    }, { passive: true });
  }

  initWishlist() {
    let wishlist = new Set(JSON.parse(localStorage.getItem('nf_wish') || '[]'));
    const updateBadges = () => {
      const count = wishlist.size;
      document.querySelectorAll('[data-wishlist-count]').forEach(el => {
        el.textContent = count;
        el.style.display = count === 0 ? 'none' : 'grid';
      });
    };

    document.addEventListener('click', e => {
      const btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      const productId = btn.dataset.productId;
      if (!productId) return;
      if (wishlist.has(productId)) wishlist.delete(productId);
      else wishlist.add(productId);
      localStorage.setItem('nf_wish', JSON.stringify([...wishlist]));
      updateBadges();
      // Toggle icon
      const empty = btn.querySelector('.wish-icon-empty');
      const full = btn.querySelector('.wish-icon-full');
      if (empty && full) {
        if (wishlist.has(productId)) { empty.style.display = 'none'; full.style.display = ''; }
        else { empty.style.display = ''; full.style.display = 'none'; }
      }
      btn.classList.toggle('active', wishlist.has(productId));
    });

    updateBadges();
    // Initialize active state
    document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      const productId = btn.dataset.productId;
      if (productId && wishlist.has(productId)) {
        btn.classList.add('active');
        const empty = btn.querySelector('.wish-icon-empty');
        const full = btn.querySelector('.wish-icon-full');
        if (empty && full) { empty.style.display = 'none'; full.style.display = ''; }
      }
    });
  }

  initQuickAdd() {
    document.addEventListener('click', async e => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();
      const variantId = btn.dataset.variantId;
      const productId = btn.dataset.productId;
      if (!variantId) {
        // If no variant ID, redirect to product page
        const link = btn.closest('.product-card')?.querySelector('a')?.href;
        if (link) window.location.href = link;
        return;
      }
      btn.disabled = true;
      const original = btn.textContent;
      btn.textContent = '…';
      await this.addToCart(variantId, 1);
      btn.textContent = original;
      btn.disabled = false;
    });
  }

  initFilters() {
    // Simple client-side filter for collection page if Shopify filters not used
    const filterBtns = document.querySelectorAll('[data-filter]');
    if (!filterBtns.length) return;
    filterBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const filter = btn.dataset.filter;
        filterBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        document.querySelectorAll('#collection-products .product-card, #collection-grid .product-card').forEach(card => {
          const cat = card.dataset.cat || card.getAttribute('data-cat') || '';
          const show = filter === 'all' || cat.includes(filter) || card.textContent.toLowerCase().includes(filter);
          card.style.display = show ? 'flex' : 'none';
        });
      });
    });
  }

  formatMoney(cents) {
    const moneyFormat = window.Shopify && window.Shopify.routes ? (window.theme && window.theme.moneyFormat) : '${{amount}}';
    // Simple fallback
    if (typeof cents === 'string') cents = parseInt(cents);
    const amount = (cents / 100).toFixed(2);
    // Try Shopify.formatMoney if available
    if (window.Shopify && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, '${{amount}}');
    }
    return `$${amount}`;
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }
}

// Initialize theme when DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new NorthFormTheme());
} else {
  new NorthFormTheme();
}

// Expose for global
window.NorthFormTheme = NorthFormTheme;

// Global settings from Liquid (set in theme.liquid if needed)
window.NorthformSettings = window.NorthformSettings || {
  freeShippingThreshold: 7500
};
